import {
  convertDateToYYYYMMDDFormat,
  convertDateToYYYYMMFormat,
  convertEventSelectToTarget,
  CustomDateRangePicker,
  formatDataToLabelValue,
  FormFeedBack,
  NoDataComponent,
  PageWrapper,
  Select,
  STATUSPAGE,
  TableVirtualized,
  TableWithGroupingsLoadingSkeleton,
} from '@repo/ui';
import {
  GetAllStudentsSelectOptionsUseCaseInterface,
  HandleReportsAnalyticalActivitiesByStudent,
} from 'domain/usecases';
import { FormikValues, useFormik } from 'formik';
import noData from 'presentation/app/assets/images/no-task.png';
import { notifyError } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { makeAnalyticalActivitiesByStudentsColumns } from 'presentation/app/modules/Reports/common/columns/activities/analytical-activities-by-students-columns';
import { ReportsGeneralFilters } from 'presentation/app/modules/Reports/components/reports-general-filters';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearReportsAnalyticalActivities,
  loadReportsAnalyticalActivities,
  loadStudentsSelectOptions,
} from 'presentation/config/store/reports/handle-reports-analytical-activities-by-sturendt';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Card, Col, FormGroup, Input, Label, Row, Spinner } from 'reactstrap';
import { v4 as uuid } from 'uuid';
import * as Yup from 'yup';

const ReportsAnalyticalActivitiesByStudents = () => {
  // States
  const [buttonSelectedSubmit, setButtonSelectedSubmit] = useState<string>();
  const [statusGet, setStatusGet] = useState<STATUSPAGE>();
  const [hasFetchedData, setHasFetchedData] = useState(false);

  // Hooks
  const { reportsAnalyticalActivities, studentService } = useService();
  const dispatch = useDispatch();

  const { studentsSelectOptions } = useAppSelector(
    (state) => state.reportsAnalyticalActivities,
  );

  const { data: handleAnalyticalActivitiesResponse } = useAppSelector(
    (state) => state.reportsAnalyticalActivities,
  );

  //Requests

  // Gerar PDF, EXCEL e JSON.
  const requestAnalyticalActivities = useCallback(
    async (params?: HandleReportsAnalyticalActivitiesByStudent.Params) => {
      setStatusGet(STATUSPAGE.LOADING);

      await reportsAnalyticalActivities
        .handleReportsAnalyticalActivitiesByStudent(params)
        .then((res: any) => {
          let blob = new Blob([res], {
            type: `application/${params?.type === 'PDF' ? 'pdf' : 'vnd.openxmlformats-officedocument.spreadsheetml.sheet'}`,
          });
          let link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          window.open(link.href, '_blank');
        })
        .catch((err) => console.log(err))
        .finally(() => {
          setStatusGet(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestAnalyticalActivitiesJson = useCallback(
    async (params?: HandleReportsAnalyticalActivitiesByStudent.Params) => {
      setStatusGet(STATUSPAGE.LOADING);
      await reportsAnalyticalActivities
        .handleReportsAnalyticalActivitiesByStudent(params)
        .then((res: any) => {
          dispatch(loadReportsAnalyticalActivities(res));
        })
        .catch((err) => {
          dispatch(clearReportsAnalyticalActivities());
          notifyError(err.message);
          console.log(err);
        })
        .finally(() => {
          setStatusGet(STATUSPAGE.DONE);
          setHasFetchedData(true);
        });
    },
    [],
  );

  // Listagem de Estudantes
  const requestGetAllStudentsSelectOptions = useCallback(
    async (params?: GetAllStudentsSelectOptionsUseCaseInterface.Params) => {
      await studentService
        .getAllStudentsSelectOptions(params)
        .then((res) => {
          dispatch(loadStudentsSelectOptions(res));
        })
        .catch((err) => console.log(err));
    },
    [],
  );

  function getFirstDayOfMonth() {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  }

  function getCurrentDate() {
    return new Date();
  }

  // Preparar parâmetros para validation
  const requestParams = (values: FormikValues, search: string = '') => {
    const groupsValues = values.groups.map((group) => group.value);
    const studentsValues = values.students.map((student) => student.value);
    const activityStatusValues = values?.activityStatus?.map(
      (activityStatus) => activityStatus.value,
    );

    return {
      entity: values?.entity?.value,
      course: values.course?.value,
      groups: groupsValues,
      starts: values.starts,
      ends: values.ends,
      students: studentsValues,
      curricularUnit: values.unit?.value,
      situations: activityStatusValues,
      semester: values?.semester?.value,
      activities: values.validation,
      search,
    };
  };

  //Validation
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      groups: [],
      ends: convertDateToYYYYMMDDFormat(getCurrentDate()),
      unit: null,
      students: [],
      entity: null,
      course: null,
      starts: convertDateToYYYYMMDDFormat(getFirstDayOfMonth()),
      semester: {
        value: null,
      },
      validation: null,
      activityStatus: [],
      period: convertDateToYYYYMMFormat(new Date()).slice(0, 7),
    },
    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      unit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      groups: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      students: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
      activityStatus: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),

      semester: Yup.object()
        .shape({
          label: Yup.string().nullable(),
          value: Yup.string().nullable(),
        })
        .nullable(),
      validation: Yup.string().nullable(),
      starts: Yup.date().required('Data é obrigatória.'),
      ends: Yup.date().required('Data é obrigatória.'),
    }),

    onSubmit: (values) => {
      const params = requestParams(values);

      if (buttonSelectedSubmit === 'search-button-json') {
        requestAnalyticalActivitiesJson(params);
      } else if (buttonSelectedSubmit === 'export-pdf') {
        requestAnalyticalActivities({ ...params, type: 'PDF' });
      } else if (buttonSelectedSubmit === 'export-excel') {
        requestAnalyticalActivities({ ...params, type: 'EXCEL' });
      }
    },
  });

  useEffect(() => {
    requestGetAllStudentsSelectOptions({
      course: validation?.values?.course?.value,
      groups: validation?.values?.groups?.map((group) => group?.value),
      entity: validation?.values?.entity?.value,
      curricularUnit: validation?.values?.unit?.value,
      semester: validation?.values?.semester?.value,
    });
  }, [
    validation?.values?.unit,
    validation?.values?.course,
    validation?.values?.groups,
    validation?.values?.entity,
    validation?.values?.semester?.value,
  ]);

  const formattedData = handleAnalyticalActivitiesResponse?.reduce(
    (acc, student) => [
      ...acc,
      {
        id: uuid(),
        isHeader: true,
        title: (
          <div className="d-flex justify-content-between">
            <span>{student?.student}</span> <span>{student?.course}</span>
          </div>
        ),
      },
      ...student?.activities?.map((activity) => ({
        id: uuid(),
        ...activity,
        hasFooter: !!activity?.feedback,
        footerText: activity?.feedback
          ? `Feedback: ${activity?.feedback}`
          : null,
      })),
    ],
    [],
  );

  return (
    <PageWrapper title="Analítico">
      <Col className="mb-3 border-0 d-flex flex-column">
        <h4>Analítico</h4>
        <p className="text-muted mb-0">
          Consulte o relatório analítico de atividades por estudante.
        </p>
      </Col>
      <Card>
        <ReportsGeneralFilters
          validation={validation}
          inputsChildren={
            <>
              <Col className="mb-3" md={4} lg={4} xxl={4}>
                <Label htmlFor="entity" className="form-label">
                  Estudante:
                </Label>
                <Select
                  isClearable
                  id="students"
                  name="students"
                  isMulti
                  placeholder="Todos os Estudantes"
                  value={validation.values.students as any}
                  onChange={(e) => {
                    validation.handleChange(
                      convertEventSelectToTarget('students', e),
                    );
                  }}
                  onBlur={validation.handleBlur}
                  options={formatDataToLabelValue(
                    studentsSelectOptions as never,
                    'id',
                    'name',
                  )}
                />
              </Col>
              <Col md={5} lg={6} xxl={3} className="mb-3">
                <Label htmlFor="" className="form-label">
                  Situação da Atividade:
                </Label>
                <Select
                  isClearable
                  isMulti
                  id="activityStatus"
                  name="activityStatus"
                  placeholder="Situação da Atividade"
                  value={validation.values.activityStatus as any}
                  onChange={(e) => {
                    validation.handleChange(
                      convertEventSelectToTarget('activityStatus', e),
                    );
                  }}
                  onBlur={validation.handleBlur}
                  options={[
                    {
                      label: 'Agendada',
                      value: '1',
                    },
                    {
                      label: 'Realizada',
                      value: '3',
                    },
                    {
                      label: 'Cancelada',
                      value: '4',
                    },
                    {
                      label: 'Perdida',
                      value: '7',
                    },
                    {
                      label: 'Perdida Justificada',
                      value: '8',
                    },
                  ]}
                />
              </Col>
              <Col md={7} lg={6} xxl={4}>
                <Row>
                  <Label htmlFor="initial-date" className="form-label">
                    Período:
                  </Label>

                  <CustomDateRangePicker
                    onDateChange={(start, ends) => {
                      validation.setFieldValue(
                        'starts',
                        convertDateToYYYYMMDDFormat(start),
                      );
                      validation.setFieldValue(
                        'ends',
                        convertDateToYYYYMMDDFormat(ends),
                      );
                    }}
                  />

                  <FormFeedBack
                    error={validation?.errors?.starts?.toString()}
                    isInvalid={
                      !!(validation?.touched && validation?.errors?.starts)
                    }
                  />
                </Row>
              </Col>
              <Col xxl={5} className="d-flex flex-column">
                <Label>Validação:</Label>
                <div className="d-flex gap-4 flex-wrap">
                  <FormGroup>
                    <Input
                      name="validation"
                      id="all"
                      type="radio"
                      value={null}
                      checked={!validation.values.validation as any}
                      onChange={(e) => {
                        if (e.target.checked) {
                          validation.handleChange({
                            target: {
                              name: 'validation',
                              value: null,
                            },
                          });
                        }
                      }}
                      className="form-check-input"
                      style={{ cursor: 'pointer' }}
                    />
                    <Label htmlFor="all" className="ms-1 form-check-label">
                      Todas
                    </Label>
                  </FormGroup>
                  <FormGroup>
                    <Input
                      name="validation"
                      id="validated"
                      type="radio"
                      value="1"
                      onChange={validation.handleChange}
                      className="form-check-input"
                      style={{ cursor: 'pointer' }}
                    />
                    <Label
                      htmlFor="validated"
                      className="ms-1 form-check-label"
                    >
                      Validadas
                    </Label>
                  </FormGroup>
                  <FormGroup>
                    <Input
                      name="validation"
                      id="not-validated"
                      type="radio"
                      value="2"
                      onChange={validation.handleChange}
                      className="form-check-input"
                      style={{ cursor: 'pointer' }}
                    />
                    <Label
                      htmlFor="not-validated"
                      className="ms-1 form-check-label"
                      onChange={validation.handleChange}
                    >
                      Rejeitadas
                    </Label>
                  </FormGroup>
                  <FormGroup>
                    <Input
                      name="validation"
                      id="rejected"
                      type="radio"
                      value="3"
                      onChange={validation.handleChange}
                      className="form-check-input"
                      style={{ cursor: 'pointer' }}
                    />
                    <Label htmlFor="rejected" className="ms-1 form-check-label">
                      Pendente
                    </Label>
                  </FormGroup>
                </div>
              </Col>
            </>
          }
          buttonsChildren={
            <div className="d-flex flex-wrap gap-2 justify-content-end align-content-end">
              <button
                id="search-button-json"
                type="submit"
                onClick={() => {
                  setButtonSelectedSubmit('search-button-json');
                }}
                className="btn btn-primary waves-effect waves-light"
                disabled={Boolean(statusGet)}
              >
                {buttonSelectedSubmit === 'search-button-json' &&
                Boolean(statusGet) ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Visualizar
                    <i className="ri-computer-line mx-2"></i>
                  </>
                )}
              </button>

              <button
                type="submit"
                id="export-pdf"
                onClick={() => {
                  setButtonSelectedSubmit('export-pdf');
                }}
                className="btn btn-light waves-effect waves-light"
                disabled={Boolean(statusGet)}
              >
                {buttonSelectedSubmit === 'export-pdf' && Boolean(statusGet) ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Visualizar em PDF
                    <i className="las la-file-pdf mx-2"></i>
                  </>
                )}
              </button>
              <button
                id="export-excel"
                type="submit"
                onClick={() => {
                  setButtonSelectedSubmit('export-excel');
                }}
                className="btn btn-light waves-effect waves-light"
                disabled={Boolean(statusGet)}
              >
                {buttonSelectedSubmit === 'export-excel' &&
                Boolean(statusGet) ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>
                    Exportar para Excel
                    <i className="ri-file-excel-2-line mx-2"></i>
                  </>
                )}
              </button>
            </div>
          }
        />
        {handleAnalyticalActivitiesResponse?.length === 0 &&
          hasFetchedData &&
          statusGet === STATUSPAGE.DONE && (
            <div className="p-4">
              <NoDataComponent src={noData} />
            </div>
          )}

        {statusGet === STATUSPAGE.LOADING && (
          <TableWithGroupingsLoadingSkeleton />
        )}

        {statusGet === STATUSPAGE.DONE &&
          handleAnalyticalActivitiesResponse?.length > 0 && (
            <TableVirtualized
              columns={makeAnalyticalActivitiesByStudentsColumns()}
              data={formattedData}
              // onSearch={(search) => {
              //   const params = requestParams(validation.values, search);
              //   requestAnalyticalActivitiesJson(params);
              // }}
            />
          )}
      </Card>
    </PageWrapper>
  );
};

export default ReportsAnalyticalActivitiesByStudents;
